import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Alert, Box, CloseButton, Image } from '@zillow/constellation-10';

import { AppSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import StaticMapActions from '../../actions/StaticMapActions';
import CorrectMapPinCSEvents from '../../model/ClickstreamEvent/CorrectMapPinCSEvents';

const MAP_MARKER = 'https://nodes3cdn.hotpads.com/google-maps-icons/MapMarker_home.png';

export type Props = {
  address: string;
  marginTop?: string | Record<string, string>;
  additionalMarkers?: string;
  size?: string;
  scale?: string | number;
  showImpreciseLocationAlert?: boolean;
  isMultiplexType?: boolean;
};

const DEFAULT_MAP_SIZE = '388x291';

const StaticMap = ({
  address,
  marginTop,
  additionalMarkers,
  size = DEFAULT_MAP_SIZE,
  scale,
  showImpreciseLocationAlert = false,
  isMultiplexType = false,
}: Props) => {
  const dispatch = useDispatch();
  const [mapUrl, setMapUrl] = useState('');

  const isScreenLgGTE = useSelector((state) => AppSelectors.makeIsScreenLgGTE(state));
  const user = useSelector((state) => state.user);

  const confirmAddressButtonTitle = isMultiplexType ? 'Yes, continue' : 'Confirm';

  useEffect(() => {
    const encodedAddress = encodeURIComponent(address);
    const mapSize = showImpreciseLocationAlert ? (isScreenLgGTE ? '388x388' : '388x554') : size;
    const gmapsAPIParams = new URLSearchParams({
      center: encodedAddress,
      size: mapSize,
      style: 'feature:poi|visibility:off',
      markers: `${additionalMarkers ? `${additionalMarkers}` : ''}&markers=icon:${MAP_MARKER}|${encodedAddress}`,
      ...(scale && { scale: String(scale) }),
    });

    dispatch(
      StaticMapActions.signUrl(`https://maps.googleapis.com/maps/api/staticmap?${gmapsAPIParams.toString()}`, {
        brandName: 'zillow',
      }),
    )
      // @ts-expect-error
      .then((res: string) => setMapUrl(res));
  }, [isScreenLgGTE, showImpreciseLocationAlert]);

  useEffect(() => {
    if (showImpreciseLocationAlert) {
      CorrectMapPinCSEvents.ViewImpreciseLocationAlert({ user });
    }
  }, [showImpreciseLocationAlert]);

  return (
    Boolean(mapUrl) && (
      <>
        {showImpreciseLocationAlert ? (
          <Box
            css={{
              position: 'relative',
              display: 'inline-block',
              flex: size !== DEFAULT_MAP_SIZE ? 'none' : { lg: '1 1 0' },
              width: { base: 'fit-content', lg: 'auto' },
            }}
          >
            <Alert
              tone="warning"
              role="alert"
              closeButton={<CloseButton />}
              css={{
                position: 'absolute',
                top: '100',
                zIndex: 2,
                margin: '0px {spacing.200}',
              }}
              data-testid="StaticMap-ImpreciseLocationAlert"
              data-eventid={CorrectMapPinCSEvents.ViewImpreciseLocationAlert.data_event_id}
              data-eventdescription="View alert event"
            >
              If the address is correct but the location is wrong, select &quot;{confirmAddressButtonTitle}&quot; to
              proceed. We&apos;ll fix it in an upcoming step.
            </Alert>
            <Image src={mapUrl} alt={address} css={{ marginTop, width: { lg: '100%' } }} />
          </Box>
        ) : (
          <Image src={mapUrl} alt={address} css={{ marginTop }} />
        )}
      </>
    )
  );
};

export default StaticMap;
